import { Box, MenuItem, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import {
  CreateAndSendExternalInvitationSurveyParams,
  createExternalInvitationSurvey,
} from '@/domain/external-invitation-survey/actions/create-external-invitation-survey.action';
import { checkUserCanCreateSurvey } from '@/domain/external-invitation-survey/external-invitation-survey.selectors';
import { PositionGeocode } from '@/domain/position-geocode/position-geocode.entity';
import { ProjectAbstract } from '@/domain/project/entities/project.entity';
import { selectProjectFestivalItems } from '@/domain/project-festival/project-festival.selectors';
import { selectProjectTourItems } from '@/domain/project-tour/project-tour.selectors';
import { selectProjectVenuesItems } from '@/domain/project-venue/project-venue.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSPositionGeocodeField } from '@/ui/components/input/FSPositionGeocodeField';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

type Inputs = {
  eventPosition: PositionGeocode | undefined;
  title: string;
  projectId: number | undefined;
};

export function CreateNewSurveyForm() {
  const dispatch = useAppDispatch();
  const festivals = useAppSelector(selectProjectFestivalItems);
  const venues = useAppSelector(selectProjectVenuesItems);
  const tours = useAppSelector(selectProjectTourItems);
  const projects: ProjectAbstract[] = [...festivals, ...venues, ...tours];
  const canCreateSurvey = useAppSelector(checkUserCanCreateSurvey);

  const defaultValues: Inputs = {
    eventPosition: undefined,
    title: '',
    projectId: undefined,
  };
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Inputs>({ defaultValues });

  const onSubmit = (data: Inputs) => {
    if (!data.projectId) return;
    const params: CreateAndSendExternalInvitationSurveyParams = {
      eventPosition: data.eventPosition,
      title: data.title,
      projectId: data.projectId,
      scope: 'SURVEY-MOBILITY-ACCOMMODATION',
    };
    dispatch(createExternalInvitationSurvey(params));
    reset();
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack sx={{ flexDirection: 'row', gap: 1, alignItems: 'center' }}>
          <FSTextField
            sx={{ width: '15rem' }}
            label={translate('Surveys.create-new-survey-form.title')}
            register={{ ...register('title', { required: true }) }}
            error={!!errors.title}
            helperText={errors.title?.message}
          />
          <Controller
            name="eventPosition"
            rules={{ required: true }}
            control={control}
            render={({ field }) => (
              <FSPositionGeocodeField
                sx={{ width: '15rem' }}
                label={translate(
                  'Surveys.create-new-survey-form.event-position',
                )}
                value={field.value}
                required={true}
                onChange={field.onChange}
                error={!!errors.eventPosition}
                helperText={errors.eventPosition?.message}
              />
            )}
          />
          <Controller
            name={'projectId'}
            rules={{ required: true }}
            control={control}
            render={({ field }) => (
              <FSTextField
                sx={{ width: '15rem' }}
                select
                label={translate(
                  'Surveys.create-new-survey-form.linked-project',
                )}
                value={field.value ?? ''}
                onChange={field.onChange}
                error={!!errors.projectId}
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {translate(`ProjectType.${project.type}`)} : {project.name}
                  </MenuItem>
                ))}
              </FSTextField>
            )}
          />
          <FSPrimaryButton type="submit" disabled={!canCreateSurvey}>
            {translate('Surveys.create')}
          </FSPrimaryButton>
        </Stack>
        {!canCreateSurvey && (
          <Typography color="warning.main">
            {translate('Surveys.create-new-survey-form.limit-reached')}
          </Typography>
        )}
      </form>
    </Box>
  );
}
